import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, EyeIcon, PencilIcon, FunnelIcon, TrashIcon, ArrowPathIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import DataTable from '../../components/common/DataTable';
import CasesModuleNav from '../../components/cases/CasesModuleNav';
import { useAuth } from '../../contexts/AuthContext';

const statusConfig = {
  open: { label: 'Open', bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  active: { label: 'Active', bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  pending: { label: 'Pending', bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  closed: { label: 'Closed', bg: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
  archived: { label: 'Archived', bg: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
};

/** Safely extract an array from an Axios response that may be paginated or flat. */
const extractArray = (response) => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const CasesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Backend filters cases to those the user is assigned to for attorneys & paralegals.
  const isAssignmentScoped = user?.role === 'attorney' || user?.role === 'paralegal';
  const heading = isAssignmentScoped ? 'My Cases' : 'Cases';
  const [cases, setCases] = useState([]);
  const [allCases, setAllCases] = useState([]); // unfiltered, used for stats bar
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [toast, setToast] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchCases = useCallback(async (status) => {
    try {
      setLoading(true);
      const url = status ? `/api/cases/?status=${status}` : '/api/cases/';
      const response = await api.get(url);
      const fetched = extractArray(response);
      setCases(fetched);
      if (!status) setAllCases(fetched); // keep unfiltered snapshot for stats
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch cases.');
      console.error('Failed to fetch cases:', err);
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCases(statusFilter);
  }, [statusFilter, fetchCases]);

  // Compute status counts from unfiltered list
  const statusCounts = useMemo(() => {
    const counts = { open: 0, active: 0, pending: 0, closed: 0, archived: 0 };
    allCases.forEach((c) => {
      const s = c.status?.toLowerCase();
      if (s && counts[s] !== undefined) counts[s]++;
    });
    return counts;
  }, [allCases]);

  const handleDelete = async (caseItem) => {
    setDeletingId(caseItem.id);
    try {
      await api.delete(`/api/cases/${caseItem.id}/`);
      setCases((prev) => prev.filter((c) => c.id !== caseItem.id));
      setAllCases((prev) => prev.filter((c) => c.id !== caseItem.id));
      showToast(`"${caseItem.title}" deleted successfully.`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete case.', 'error');
    } finally {
      setDeletingId(null);
      setShowDeleteModal(null);
    }
  };

  const handleQuickStatusChange = async (caseItem, newStatus) => {
    try {
      await api.patch(`/api/cases/${caseItem.id}/`, { status: newStatus });
      setCases((prev) =>
        prev.map((c) => (c.id === caseItem.id ? { ...c, status: newStatus } : c))
      );
      showToast(`"${caseItem.title}" status changed to ${statusConfig[newStatus]?.label || newStatus}.`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update status.', 'error');
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Case Title',
      render: (row) => (
        <Link to={`/cases/${row.id}`} className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400">
          {row.title}
        </Link>
      ),
    },
    {
      key: 'client',
      label: 'Client',
      render: (row) =>
        row.client_details
          ? `${row.client_details.first_name} ${row.client_details.last_name}`
          : row.client || 'N/A',
      sortValue: (row) =>
        row.client_details
          ? `${row.client_details.first_name} ${row.client_details.last_name}`
          : row.client || '',
    },
    {
      key: 'case_type',
      label: 'Type',
      render: (row) => row.case_type || 'N/A',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const cfg = statusConfig[row.status?.toLowerCase()] || statusConfig.open;
        return (
          <select
            value={row.status?.toLowerCase() || 'open'}
            onChange={(e) => handleQuickStatusChange(row, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={`px-2 py-0.5 text-xs font-semibold rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-primary-500 ${cfg.bg}`}
            title="Click to change status"
          >
            {Object.entries(statusConfig).map(([value, { label }]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        );
      },
    },
    {
      key: 'last_updated',
      label: 'Last Updated',
      render: (row) =>
        row.last_updated ? new Date(row.last_updated).toLocaleDateString() : 'N/A',
      sortValue: (row) => (row.last_updated ? new Date(row.last_updated).getTime() : 0),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      className: 'text-right',
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Link
            to={`/cases/${row.id}`}
            className="p-1.5 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            title="View case"
          >
            <EyeIcon className="h-4 w-4" />
          </Link>
          <Link
            to={`/cases/${row.id}/edit`}
            className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Edit case"
          >
            <PencilIcon className="h-4 w-4" />
          </Link>
          <button
            onClick={(e) => { e.stopPropagation(); setShowDeleteModal(row); }}
            disabled={deletingId === row.id}
            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-colors disabled:opacity-50"
            title="Delete case"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading cases...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 text-lg">Error: {error}</p>
        <p className="text-gray-600 dark:text-gray-400">Could not load case data. Please try again later.</p>
        <button
          onClick={() => fetchCases(statusFilter)}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50"
        >
          <ArrowPathIcon className="h-4 w-4" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Sub-navigation */}
      <CasesModuleNav openCount={statusCounts.open} activeCount={statusCounts.active} />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all animate-slide-in ${
          toast.type === 'error'
            ? 'bg-red-600 text-white'
            : 'bg-green-600 text-white'
        }`}>
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">
            &times;
          </button>
        </div>
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="cases-stats-bar">
        {[
          { key: 'open',     label: 'Open',     color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',   text: 'text-blue-700 dark:text-blue-300',   badge: 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200' },
          { key: 'active',   label: 'Active',   color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-300', badge: 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200' },
          { key: 'pending',  label: 'Pending',  color: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300', badge: 'bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-200' },
          { key: 'closed',   label: 'Closed',   color: 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600',   text: 'text-gray-600 dark:text-gray-400',   badge: 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300' },
          { key: 'archived', label: 'Archived', color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800', text: 'text-purple-700 dark:text-purple-300', badge: 'bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200' },
        ].map(({ key, label, color, text, badge }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer hover:shadow-sm ${color} ${
              statusFilter === key ? 'ring-2 ring-primary-500 ring-offset-1' : ''
            }`}
            title={`Filter by ${label}`}
            aria-pressed={statusFilter === key}
          >
            <span className={`text-xs font-medium ${text}`}>{label}</span>
            <span className={`text-lg font-bold rounded-lg px-2 py-0.5 ${badge}`}>
              {statusCounts[key]}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{heading}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {cases.length} case{cases.length !== 1 ? 's' : ''} {statusFilter ? `with status "${statusConfig[statusFilter]?.label}"` : 'found'}
            {isAssignmentScoped && ' — showing cases assigned to you'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Refresh */}
          <button
            onClick={() => fetchCases(statusFilter)}
            disabled={loading}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title="Refresh cases"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500 py-1.5"
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="closed">Closed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <Link
            to="/cases/new"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg shadow-sm hover:bg-primary-700 focus:outline-none transition-colors"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Case
          </Link>
        </div>
      </div>

      {cases.length === 0 && !loading ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <BriefcaseIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No cases yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            {statusFilter
              ? `No ${statusConfig[statusFilter]?.label || statusFilter} cases found. Try a different filter.`
              : 'Create your first case to start managing your legal matters.'}
          </p>
          {!statusFilter && (
            <Link
              to="/cases/new"
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg shadow-sm hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" /> Create Your First Case
            </Link>
          )}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={cases}
          searchPlaceholder="Search cases by title, type, or status..."
          searchKeys={['title', 'case_type', 'status']}
          emptyMessage="No matching cases found."
          defaultSort={{ key: 'last_updated', direction: 'desc' }}
          onRowClick={(row) => navigate(`/cases/${row.id}`)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !deletingId && setShowDeleteModal(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Case</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>"{showDeleteModal.title}"</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                disabled={!!deletingId}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal)}
                disabled={!!deletingId}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center"
              >
                {deletingId ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4 mr-2" /> Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CasesList;
